import { COMMERCE_ERROR, CommerceError, defineAdapter } from '@loop-coffee/commerce-contracts';

import { listCheckoutPaymentOptions, resolvePaymentAdapter } from './registry';

// Glue between the registry and the checkout payment slot (`payment` of CommerceProvider:
// `{ id, label, flow, capabilities, prepare(cart) }`). Nothing here knows a provider by name.

const SUPPORTED_FLOWS = ['none', 'redirect'];

/** Region payment options the storefront can present, in API order. */
export async function loadCheckoutPaymentOptions(client, regionId) {
  return listCheckoutPaymentOptions(await client.listPaymentProviders(regionId));
}

/** The hosted page URL as a string when it is an absolute https URL, otherwise null. */
export function safeRedirectUrl(value) {
  if (typeof value !== 'string' || !value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

/** Hosted payment page the backend put in the provider's payment session (`data.checkout_url`). */
export function readRedirectUrl(collection, providerId) {
  const sessions = (collection && collection.payment_sessions) || [];
  const session = sessions.find((s) => s && s.provider_id === providerId);
  return safeRedirectUrl(session && session.data && session.data.checkout_url);
}

/**
 * Checkout payment slot for one provider of the registry.
 *   none     - `prepare` opens the payment session; nothing else happens.
 *   redirect - `prepare` opens the session and resolves `{ collection, redirectUrl }`; it fails
 *              when the backend did not return a valid https page, so no order is placed that
 *              the customer could never pay.
 * Embedded flows need their own step and are refused here.
 */
export function createCheckoutPaymentAdapter(client, providerId) {
  const found = resolvePaymentAdapter(providerId);
  if (!found) {
    throw new Error(`Payment provider ${providerId} is not supported by this storefront`);
  }
  if (!SUPPORTED_FLOWS.includes(found.flow)) {
    throw new Error(`Payment flow "${found.flow}" is not supported by checkout yet`);
  }
  const prepare =
    found.flow === 'redirect'
      ? async (cart) => {
          const collection = await client.initiatePaymentSession(cart.id, found.id);
          const redirectUrl = readRedirectUrl(collection, found.id);
          if (!redirectUrl) {
            throw new CommerceError(
              COMMERCE_ERROR.UNAVAILABLE,
              'Não foi possível abrir a página de pagamento. Tente novamente em instantes.'
            );
          }
          return { collection, redirectUrl };
        }
      : (cart) => client.initiatePaymentSession(cart.id, found.id);

  return defineAdapter('payment', {
    id: found.id,
    label: found.label,
    description: found.description,
    flow: found.flow,
    capabilities: found.capabilities,
    testOnly: found.testOnly,
    prepare,
  });
}
